import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Eye, ShoppingCart, BarChart3, ArrowRight, Code2, Zap,
  FileJson, Beaker, HelpCircle, CheckCircle
} from 'lucide-react';

export default function DocsOverview() {
  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/20 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Developer Documentation
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Commerce Index Developer Docs
        </h1>
        <p className="text-lg text-[#94A3B8] mb-6">
          Integrate view & purchase events, enable social proof, and power richer commerce signals.
        </p>
        <div className="flex gap-4">
          <Link to="/docs/quickstart">
            <Button className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90" data-testid="docs-quickstart-btn">
              Quick Start Guide <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/docs/rest-api">
            <Button variant="outline" className="border-[#334155] text-[#E2E8F0] hover:bg-[#1E293B]" data-testid="docs-api-ref-btn">
              API Reference
            </Button>
          </Link>
        </div>
      </div>

      {/* What This Integration Covers */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What This Integration Covers
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <Eye className="w-8 h-8 text-[#3EF1C7] mb-2" />
              <CardTitle className="text-white text-lg">Product Views</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Track when shoppers view your product pages to show live activity.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <ShoppingCart className="w-8 h-8 text-[#7A5CFF] mb-2" />
              <CardTitle className="text-white text-lg">Product Purchases</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Track completed orders to display recent purchase activity.
              </p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardHeader className="pb-2">
              <BarChart3 className="w-8 h-8 text-[#FFD166] mb-2" />
              <CardTitle className="text-white text-lg">Proof API</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[#94A3B8]">
                Retrieve aggregated view and purchase counts for any product.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Who It's For */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Who It's For
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { title: 'Shopify Developers', desc: 'Theme and app developers integrating with Shopify stores' },
            { title: 'Headless Commerce', desc: 'Next.js, Gatsby, or custom frontend implementations' },
            { title: 'Custom Platforms', desc: 'Any e-commerce platform with custom checkout flows' }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-4 bg-[#1E293B] rounded-lg border border-[#334155]">
              <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-white font-medium mb-1">{item.title}</h3>
                <p className="text-sm text-[#94A3B8]">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Key Benefits */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          What You Get
        </h2>
        <div className="space-y-4">
          {[
            {
              icon: '👁️',
              title: 'Show live proof',
              desc: '"X people viewed this / Y purchased this" directly on your PDPs'
            },
            {
              icon: '📊',
              title: 'Feed CI with real events',
              desc: 'Unlock benchmarking and category-level insights later'
            },
            {
              icon: '⚡',
              title: 'Works with existing stack',
              desc: 'No major changes needed - just a tiny JS snippet'
            }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4 p-4 bg-[#0D1628] rounded-lg border border-[#22304A]">
              <span className="text-2xl">{item.icon}</span>
              <div>
                <h3 className="text-white font-medium mb-1">{item.title}</h3>
                <p className="text-sm text-[#94A3B8]">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Documentation Sections */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Documentation Sections
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { href: '/docs/welcome', icon: Zap, title: 'Platform Overview', desc: 'The full Commerce Index platform — agents, engines, Money Found' },
            { href: '/docs/channels-signup', icon: HelpCircle, title: 'Channels & Signup', desc: 'WhatsApp/SMS/iMessage signup + how the OS reaches you' },
            { href: '/docs/quickstart', icon: Zap, title: 'Quick Start', desc: 'Get up and running in under 30 minutes' },
            { href: '/docs/events', icon: FileJson, title: 'Events', desc: 'Learn about view and purchase event models' },
            { href: '/docs/js-api', icon: Code2, title: 'JavaScript API', desc: 'Browser snippet reference' },
            { href: '/docs/rest-api', icon: FileJson, title: 'REST API', desc: 'Server-side API endpoints' },
            { href: '/docs/examples', icon: Beaker, title: 'Examples & Recipes', desc: 'Platform-specific guides' },
            { href: '/docs/faq', icon: HelpCircle, title: 'FAQ', desc: 'Common questions answered' },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <Link key={item.href} to={item.href}>
                <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/50 transition-colors cursor-pointer h-full">
                  <CardContent className="p-4 flex items-start gap-4">
                    <Icon className="w-6 h-6 text-[#3EF1C7] flex-shrink-0" />
                    <div>
                      <h3 className="text-white font-medium mb-1">{item.title}</h3>
                      <p className="text-sm text-[#94A3B8]">{item.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
